import { FontSizeContext } from '@contexts/fontsize'
import IconQuote from '@fortuneo/ui-icons/pictograms/IconQuote'
import { withDisplayName } from '@hocs/with-display-name'
import RichText from '@resolvers/rich-text'
import { defaultRichTextFieldOptionsDeprecated } from '@resolvers/rich-text/default-rich-text-field-options-deprecated'
import {
  contentArticleImageTextOptions,
  contentArticleTitleOptions,
} from '@rich-text/contentful-content-article-rich-text'
import { Card } from '@ui/card/card'
import { EnhancedButton } from '@ui/enhanced-components/enhanced-components'
import {
  ENHANCED_COMPONENT_ROLE,
  ENHANCED_COMPONENT_TYPE,
} from '@ui/enhanced-components/types'
import { graphql } from 'gatsby'
import React from 'react'

const ContentfulContentArticleCardFormule: Utils.EntryFC<
  Queries.ContentfulContentArticleCardFormuleFragment
> = ({
  content: {
    contentArticleTitle: title,
    subtitle,
    image,
    imageText,
    content,
    contentLinkText,
    contentLinkUrl,
  },
  fieldInspectorProps,
}) => {
  const hasHeader = title || subtitle || image || imageText
  const hasFooterLink = contentLinkText && contentLinkUrl
  const isClickable = !contentLinkText && contentLinkUrl

  return (
    <Card href={contentLinkUrl} isClickable={Boolean(isClickable)}>
      <div className="flex h-full flex-col justify-between p-10">
        <div>
          {hasHeader && (
            <div className="flex w-full flex-col items-center justify-center pb-10 text-center tablet:pb-6">
              {title && (
                <RichText
                  {...fieldInspectorProps({
                    fieldId: 'title',
                  })}
                  content={title}
                  options={contentArticleTitleOptions}
                />
              )}
              <IconQuote className="fill-theme-primary-main" />
              {subtitle && (
                <div className="py-3">
                  <RichText
                    {...fieldInspectorProps({
                      fieldId: 'subtitle',
                    })}
                    content={subtitle}
                    options={defaultRichTextFieldOptionsDeprecated}
                  />
                </div>
              )}
              {imageText && (
                <FontSizeContext.Provider value={6}>
                  <RichText
                    {...fieldInspectorProps({
                      fieldId: 'imageText',
                    })}
                    content={imageText}
                    options={contentArticleImageTextOptions}
                  />
                </FontSizeContext.Provider>
              )}
            </div>
          )}
          {content && (
            <RichText
              {...fieldInspectorProps({
                fieldId: 'content',
              })}
              content={content}
              options={defaultRichTextFieldOptionsDeprecated}
            />
          )}
        </div>
        {hasFooterLink && (
          <div className="pt-10">
            <EnhancedButton
              className="!m-auto !flex !min-h-[40px] !w-full !max-w-[334px] !px-8 !py-0 [&>div]:overflow-visible"
              data-role={ENHANCED_COMPONENT_ROLE.SECONDARY}
              data-type={ENHANCED_COMPONENT_TYPE.CTA}
              href={contentLinkUrl}
              variant="secondary"
              {...fieldInspectorProps({
                fieldId: 'contentLinkText',
              })}
            >
              {contentLinkText}
            </EnhancedButton>
          </div>
        )}
      </div>
    </Card>
  )
}

export const query = graphql`
  fragment ContentfulContentArticleCardFormule on Contentful4TmD9EihHakmKMkEwKaUwQ {
    ...ContentfulEntry
    contentArticleTitle: title {
      ...ContentfulContentArticleTitle
    }
    subtitle {
      ...ContentfulContentArticleSubtitle
    }
    content {
      ...ContentfulContentArticleContent
    }
    image {
      ...ContentfulDefaultAsset
    }
    imageText {
      ...ContentfulContentArticleImageText
    }
    contentLinkText
    contentLinkUrl
  }
`

export default withDisplayName(
  ContentfulContentArticleCardFormule,
  'ContentfulContentArticleCardFormule'
)
